import React, { createContext, useReducer, FunctionComponent } from 'react'
import MusicReducer, { MusicState, initialState, MusicActionType } from '@reducers/MusicReducer'
import API from '@api/musicbrainz'
import { AppStatus, ResponseMeta } from '@models/Common'
import { ArtistEntry, AlbumEntry } from '@models/Music'

export type MusicContext = MusicState & {
  searchArtist(term: string): void
  selectArtist(artist?: ArtistEntry): void
  lookupAlbums(artistId: string, name: string): void
  selectAlbum(album?: AlbumEntry): void
  selectGenre(genre?: string): void
}

export const initialMusicContext: MusicContext = {
  ...initialState,
  searchArtist: () => {},
  selectArtist: () => {},
  lookupAlbums: () => {},
  selectAlbum: () => {},
  selectGenre: () => {},
}

export const musicContext = createContext<MusicContext>(initialMusicContext)

export default function WithMusic<P extends JSX.IntrinsicAttributes>(
  WrappedComponent: FunctionComponent<P>,
): FunctionComponent<P> {
  const ComponentWithMusic: FunctionComponent<P> = (props) => {
    const [state, dispatch] = useReducer(MusicReducer, initialState)

    const searchArtist = (term: string) => {
      if (!term) {
        return
      }

      dispatch({ type: MusicActionType.SearchArtistPending, payload: term })
      if (!term) {
        return dispatch({ type: MusicActionType.SearchArtistSuccess, payload: [] })
      }
      API.searchArtist(term)
        .then((searchResults) => {
          dispatch({ type: MusicActionType.SearchArtistSuccess, payload: searchResults })
        })
        .catch((error: ResponseMeta) => {
          dispatch({ type: MusicActionType.SearchArtistFailure, payload: error.message })
        })
    }

    const lookupAlbums = (artistId: string, name: string) => {
      if (state.status === AppStatus.PENDING) {
        return
      }

      dispatch({ type: MusicActionType.LookupAlbumsPending, payload: artistId })
      API.lookupAlbums(artistId, state.artist, name)
        .then((searchResults) => {
          dispatch({ type: MusicActionType.LookupAlbumsSuccess, payload: searchResults })
          dispatch({ type: MusicActionType.SelectAlbum, payload: searchResults[0] })
        })
        .catch((error: ResponseMeta) => {
          dispatch({ type: MusicActionType.LookupAlbumsFailure, payload: error.message })
        })
    }

    const selectArtist = (artist: ArtistEntry) => {
      dispatch({ type: MusicActionType.SelectArtist, payload: artist })
      lookupAlbums(artist.id, artist.name)
    }

    const selectAlbum = (album?: AlbumEntry) => {
      dispatch({ type: MusicActionType.SelectAlbum, payload: album })
    }

    const selectGenre = (genre?: string) => {
      dispatch({ type: MusicActionType.SelectGenre, payload: genre })
    }

    return (
      <musicContext.Provider
        value={{
          ...state,
          searchArtist,
          selectArtist,
          lookupAlbums,
          selectAlbum,
          selectGenre,
        }}>
        <WrappedComponent {...props} />
      </musicContext.Provider>
    )
  }
  return ComponentWithMusic
}
